import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  FileText, Lock, Calculator, Coins, Download, Upload, CheckCircle2, RefreshCw, Landmark,
} from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PageHead, Panel, Register, RailList, RailRow, FigureStrip, Figure, DetailDrawer, DataTable,
  AcStatus, Currency, Deadline, Field, FormRow, fmtDate, money, monthLabel,
} from './design';
import AfterClaiming from './AfterClaiming';

// Claims are built from the statements for a month, so the period is a statement month (YYYY-MM).
// The server refuses anything else rather than claim $0 for a month it cannot resolve. Claims are
// made in arrears, so the month to work on is the one just finished.
const lastMonth = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
// Today in Sydney, the day the portal steps happen in.
const sydneyToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());

// Support at Home is claimed in arrears and the claim is due 60 days after the end of the quarter
// the month falls in. Only a YYYY-MM period can be placed in a quarter; anything else gets no
// deadline rather than a wrong one.
// The published dates: 30 May, 29 August, 29 November and 28 February (60 days after 31 December
// would be 1 March, but the department's date for that quarter is 28 February).
const QUARTER_DUE = { 1: ['05-30', 0], 2: ['08-29', 0], 3: ['11-29', 0], 4: ['02-28', 1] };
const claimDeadline = (period) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  if (!m) return null;
  const [md, yearOffset] = QUARTER_DUE[Math.ceil(Number(m[2]) / 3)];
  return `${Number(m[1]) + yearOffset}-${md}`;
};

const statusOf = (c) => String(c.status || '').toLowerCase();
const isSettled = (c) => ['paid', 'reconciled'].includes(statusOf(c));
const varianceOf = (c) => (c.variance != null
  ? Number(c.variance)
  : Number(c.claimed_amount || 0) - Number(c.paid_amount || 0));
// The scheme in words. It is not set on aggregate claim runs.
const schemeText = (s) => ({ SAH: 'Support at Home', HCP: 'Home Care Package' })[String(s || '').toUpperCase()] || null;

// Services Australia's budget codes, in words. A payment statement names its budgets by code; the
// code on its own means nothing to the person reading it.
const BUDGET = {
  ON: 'Ongoing budget', CM: 'Care management account', AT: 'Assistive technology', HM: 'Home modifications',
  RC: 'Restorative Care Pathway', EL: 'End-of-Life Pathway', AS: 'Assistive technology, specified needs',
};
const budgetWords = (code, text) => BUDGET[String(code || '').toUpperCase()] || text || 'Other budget';

// A load that failed, said briefly with a way to try again, where the missing thing would be.
function LoadError({ message, onRetry }) {
  return (
    <span role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-red-800">
      {message}
      {onRetry && <button type="button" onClick={onRetry} className="btn-ghost btn-sm">Try again</button>}
    </span>
  );
}

// Each period-end step's progress, expressed in the module's status words so the badge colours
// mean the same thing here as on every other tab, and in the row's mark: a clock is "needs you
// now", a tick is done.
const STEP_STATE = {
  done: { status: 'resolved', label: 'Done', severity: 'done' },
  running: { status: 'in_progress', label: 'Running', severity: 'soon' },
  ready: { status: 'open', label: 'Ready', severity: 'soon' },
  pending: { status: 'closed', label: 'Waiting on the step above', severity: 'none' },
  blocked: { status: 'closed', label: 'Waiting for a closed month', severity: 'none' },
};

// Services Australia B2G claiming settings. All config is UI-entered per org, never hardcoded:
// gateway mode + B2G organisation/client credentials (secret encrypted server-side) + NOI status.
function B2gSettings({ onSaved }) {
  const [f, setF] = useState({ gateway_mode: 'mock', b2g_org_id: '', b2g_client_id: '', b2g_client_secret: '', noi_status: '', notes: '', has_secret: false, csv_date_format: 'DD/MM/YYYY', naps_service_id: '', rounding_rule: 'nearest', one_pending: true });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const load = useCallback(() => {
    setLoadError(null);
    api.get('/aged-care/claims/settings')
      .then(r => { setF(p => ({ ...p, ...r.data, b2g_client_secret: '' })); setLoaded(true); })
      .catch(e => setLoadError(e.response?.data?.message || 'Could not load the claiming settings'));
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = k => e => setF(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/aged-care/claims/settings', f); toast.success('Claiming settings saved'); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save the claiming settings'); } finally { setSaving(false); }
  };
  // The form is only shown once the saved settings have loaded: saving the blank defaults would
  // overwrite the real ones (switching a live channel back to the simulated one, for a start).
  if (loadError) return <div className="pt-3 border-t border-slate-100"><LoadError message={loadError} onRetry={load} /></div>;
  if (!loaded) return <p className="pt-3 border-t border-slate-100 text-[12px] text-slate-500">Loading the claiming settings</p>;
  return (
    <div className="space-y-3 pt-3 border-t border-slate-100">
      <FormRow cols={3}>
        <Field label="How claims are lodged">
          {(id) => (
            <select id={id} className="input-field" value={f.gateway_mode || 'mock'} onChange={set('gateway_mode')}>
              <option value="file">Aged Care Provider Portal: import the claim file</option>
              <option value="mock">Simulated gateway, for testing</option>
              <option value="live">Live Services Australia B2G API (needs a Notice of Integration)</option>
            </select>
          )}
        </Field>
        <Field label="NOI or conformance status" hint="Optional">
          {(id) => <input id={id} className="input-field" value={f.noi_status || ''} onChange={set('noi_status')} placeholder="Not started, in conformance, or NOI granted" />}
        </Field>
        <Field label="B2G organisation ID">
          {(id) => <input id={id} className="input-field" value={f.b2g_org_id || ''} onChange={set('b2g_org_id')} placeholder="From Services Australia onboarding" />}
        </Field>
        <Field label="B2G client ID">
          {(id) => <input id={id} className="input-field" value={f.b2g_client_id || ''} onChange={set('b2g_client_id')} placeholder="API client ID" />}
        </Field>
        <Field label="B2G client secret">
          {(id) => <input id={id} type="password" className="input-field" value={f.b2g_client_secret || ''} onChange={set('b2g_client_secret')} placeholder={f.has_secret ? 'Saved. Type here to replace it.' : 'API secret'} />}
        </Field>
        <Field label="Notes" hint="Optional">
          {(id) => <input id={id} className="input-field" value={f.notes || ''} onChange={set('notes')} />}
        </Field>
        <Field label="Delivery date in the claim file" hint="Services Australia's sample file uses day/month/year. Keep it unless a test upload is refused.">
          {(id) => (
            <select id={id} className="input-field" value={f.csv_date_format || 'DD/MM/YYYY'} onChange={set('csv_date_format')}>
              <option value="DD/MM/YYYY">Day/month/year, for example 29/09/2026</option>
              <option value="YYYY-MM-DD">Year-month-day, for example 2026-09-29</option>
            </select>
          )}
        </Field>
        <Field label="NAPS service ID" hint="The service the claims are made under, from the Provider Portal. A participant's plan can name a different one.">
          {(id) => <input id={id} className="input-field" value={f.naps_service_id || ''} onChange={set('naps_service_id')} placeholder="For example 12345" maxLength={40} />}
        </Field>
        <Field label="Rounding hours to 15 minutes" hint="Services Australia only accepts quarter hours. Use the rule in your service agreements.">
          {(id) => (
            <select id={id} className="input-field" value={f.rounding_rule || 'nearest'} onChange={set('rounding_rule')}>
              <option value="nearest">To the nearest 15 minutes</option>
              <option value="down">Down to the 15 minutes before</option>
              <option value="up">Up to the next 15 minutes</option>
            </select>
          )}
        </Field>
      </FormRow>
      <label className="flex items-start gap-2 text-[13px] text-slate-700 cursor-pointer">
        <input type="checkbox" className="mt-0.5" checked={f.one_pending !== false} onChange={(e) => setF((p) => ({ ...p, one_pending: e.target.checked }))} />
        <span>
          One claim waiting for approval at a time
          <span className="block text-[12px] text-slate-500">A new claim is not lodged while another has no payment statement yet. Services Australia has not confirmed this rule; turn it off if they allow more. A claim is lodged at most once a day either way.</span>
        </span>
      </label>
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving' : 'Save claiming settings'}</button>
        <span className="text-[12px] text-slate-500">
          Secrets are encrypted at rest. The live B2G channel stays inactive until the Notice of Integration and the official API contract exist; the claim file for the Provider Portal always works.
        </span>
      </div>
    </div>
  );
}

export default function AgedCareClaims() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [claims, setClaims] = useState([]);
  const [period, setPeriod] = useState(lastMonth());
  const [gw, setGw] = useState(null); // GovClaimGateway info (mock vs live)
  const [gwError, setGwError] = useState(null);
  const [showB2g, setShowB2g] = useState(false);

  const [busy, setBusy] = useState(''); // which step is currently posting
  // The portal step being recorded for one claim: the day its invoices were submitted, or the
  // claim reference the portal gave it.
  const [portalForm, setPortalForm] = useState(null); // { id, step: 'invoices_submitted' | 'claimed', date, claimRef }

  const [contribModal, setContribModal] = useState(false);
  const [contribForm, setContribForm] = useState({ method: 'invoice', notes: '' });

  const [closeError, setCloseError] = useState(null); // the 409 that names the claim still in flight
  // A claim past its deadline needs a reason (Services Australia reviews it; it no longer holds them).
  const [late, setLate] = useState(null);             // { deadline, for: 'close' | claim id }
  const [lateReason, setLateReason] = useState('');
  const [lateCode, setLateCode] = useState('');
  const [lateCodes, setLateCodes] = useState({});     // Services Australia's late reason codes
  const [lateCodesError, setLateCodesError] = useState(null);
  const [fileCheck, setFileCheck] = useState(null);   // what the claim file will hold, and what it leaves out
  const [paidAmount, setPaidAmount] = useState('');   // reconcile by hand: the amount actually paid
  const [recon, setRecon] = useState(null);           // the last reconciliation, participant by participant
  const confirm = useConfirm();
  // Stable, or the drawer re-runs its focus effect on every render and pulls focus out of the
  // field being typed in.
  const closeRecon = useCallback(() => setRecon(null), []);
  const [afterKey, setAfterKey] = useState(0);   // reloads "After claiming" once a statement is read
  const closeContrib = useCallback(() => setContribModal(false), []);
  const closeFileCheck = useCallback(() => setFileCheck(null), []);

  // The chosen month's claim: the newest one that was not rejected (closing a month again after a
  // rejection makes a new claim). Rows arrive newest first. Every step of the period-end run acts on
  // this claim, so the file downloaded and the statement reconciled are always the month on screen.
  const monthClaim = claims.find(c => c.period === period && statusOf(c) !== 'rejected') || null;
  // Another month's claim still waiting on Services Australia, offered as a shortcut when the month
  // on screen has none (only one claim can be in flight, so it is usually the one to finish first).
  const openElsewhere = monthClaim ? null : claims.find(c => c.period !== period && !isSettled(c) && statusOf(c) !== 'rejected') || null;

  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const res = await api.get('/aged-care/claims');
      const rows = Array.isArray(res.data) ? res.data : res.data?.claims || [];
      setClaims(rows);
    } catch (e) {
      // Never an empty list on a failed load: the steps would then offer to close a month that is
      // already closed.
      setClaims([]);
      setLoadError(e.response?.data?.message || 'Could not load the claims');
    } finally {
      setLoading(false);
    }
  };
  const loadGateway = useCallback(() => {
    setGwError(null);
    api.get('/aged-care/claims/gateway').then(r => setGw(r.data))
      .catch(e => setGwError(e.response?.data?.message || 'Could not load how claims are lodged'));
  }, []);
  const loadLateCodes = useCallback(() => {
    setLateCodesError(null);
    api.get('/aged-care/sah-reference').then(r => setLateCodes(r.data?.lateReasonCodes || {}))
      .catch(e => setLateCodesError(e.response?.data?.message || "Could not load Services Australia's late reasons"));
  }, []);

  useEffect(() => {
    load();
  }, []);
  useEffect(() => { loadGateway(); loadLateCodes(); }, [loadGateway, loadLateCodes]);
  const lateBody = () => (lateCode || lateReason.trim()
    ? { lateReasonCode: lateCode || undefined, lateReason: lateReason.trim() || undefined } : {});

  // GovClaimGateway: submit the claim to Services Australia, then poll for the outcome.
  // The Provider Portal takes two steps: submit the imported invoices, then claim the submitted
  // invoices (once a day). Recording them here keeps the once-a-day and one-waiting rules.
  // `extra` is the day the invoices were submitted ({ date }) or the portal's claim reference
  // ({ claimRef }). Undo clears both steps, so it is asked first; the server refuses it once a
  // payment statement has been read for the claim.
  const portalStep = async (claim, step, extra = {}) => {
    if (step === 'undo') {
      const ok = await confirm(
        `The record that the ${monthLabel(claim.period)} invoices were submitted${claim.portal_claimed_at ? ' and claimed' : ''} in the Provider Portal is cleared${claim.portal_claim_ref ? ', with the claim reference' : ''}. Nothing changes in the portal itself.`,
        { title: 'Undo the portal steps?', confirmLabel: 'Undo portal steps', danger: true },
      );
      if (!ok) return;
    }
    setBusy('portal-' + claim.id);
    try {
      await api.post(`/aged-care/claims/${claim.id}/portal-step`, { step, ...extra });
      toast.success(step === 'claimed' ? 'Recorded as claimed in the portal' : step === 'undo' ? 'Portal steps cleared' : 'Recorded: invoices submitted in the portal');
      setPortalForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not record it'); } finally { setBusy(''); }
  };
  const savePortalForm = (claim) => {
    if (!portalForm) return;
    if (portalForm.step === 'invoices_submitted') {
      if (!portalForm.date) return toast.error('Choose the day the invoices were submitted');
      return portalStep(claim, 'invoices_submitted', { date: portalForm.date });
    }
    return portalStep(claim, 'claimed', portalForm.claimRef.trim() ? { claimRef: portalForm.claimRef.trim() } : {});
  };
  const submitToGateway = async claim => {
    if (!claim) return;
    const ok = await confirm(
      `${money(claim.claimed_amount)} for ${monthLabel(claim.period)} goes to Services Australia${gw && !gw.live ? ' through the simulated (mock) gateway' : ''}.`,
      { title: `Submit the ${monthLabel(claim.period)} claim?`, confirmLabel: 'Submit claim' },
    );
    if (!ok) return;
    setBusy('submit-' + claim.id);
    try {
      await api.post(`/aged-care/claims/${claim.id}/submit`, late?.for === claim.id ? lateBody() : {});
      toast.success('Claim submitted to the gateway');
      setLate(null); setLateReason(''); setLateCode('');
      load();
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'LATE_REASON_REQUIRED') { setLate({ deadline: d.deadline, for: claim.id }); toast.error('This claim is late. Choose the reason, then submit again.'); }
      else toast.error(d?.message || 'Could not submit the claim');
    } finally {
      setBusy('');
    }
  };
  const pollStatus = async claim => {
    if (!claim) return;
    setBusy('poll-' + claim.id);
    try {
      const r = await api.post(`/aged-care/claims/${claim.id}/poll`, {});
      const st = (r.data?.status || '').toLowerCase();
      if (st === 'paid') toast.success(`Claim paid: ${money(r.data.paid_amount)}`);
      else if (st === 'rejected') toast.error(`Claim rejected: ${r.data.rejection_reason || 'see the response code'}`);
      else toast('Still processing');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not check the status');
    } finally {
      setBusy('');
    }
  };

  const runClose = async () => {
    // Asked once. Closing again with a late reason chosen is the same decision, already agreed to.
    if (late?.for !== 'close') {
      const ok = await confirm(
        `Delivered services and travel for ${monthLabel(period)} are locked so the amounts stop changing, and the month's claim is made from them.`,
        { title: `Close actuals for ${monthLabel(period)}?`, confirmLabel: 'Close actuals' },
      );
      if (!ok) return;
    }
    setBusy('close');
    setCloseError(null);
    try {
      await api.post('/aged-care/claims/close', late?.for === 'close' ? { period, ...lateBody() } : { period });
      toast.success(`Actuals closed for ${monthLabel(period)}`);
      setLate(null); setLateReason(''); setLateCode('');
      load();
    } catch (e) {
      const d = e.response?.data;
      // 409 CLAIM_IN_FLIGHT names the claim that is blocking this one. That belongs next to the
      // action, where the operator will read it, not only in a toast that vanishes.
      if (d?.code === 'CLAIM_IN_FLIGHT') setCloseError(d.message);
      if (d?.code === 'LATE_REASON_REQUIRED') setLate({ deadline: d.deadline, for: 'close' });
      toast.error(d?.message || 'Could not close actuals');
    } finally {
      setBusy('');
    }
  };

  const runAccrue = async () => {
    setBusy('accrue');
    try {
      await api.post('/aged-care/claims/accrue-fees', { period });
      toast.success('Care management for the month totalled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not accrue fees');
    } finally {
      setBusy('');
    }
  };

  const submitContributions = async () => {
    setBusy('contributions');
    try {
      await api.post('/aged-care/claims/contributions', { period, ...contribForm });
      toast.success('Participant contributions recorded');
      setContribModal(false);
      setContribForm({ method: 'invoice', notes: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not record contributions');
    } finally {
      setBusy('');
    }
  };

  const saveBlob = (data, name) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };
  // The claim file is Services Australia's own invoice bulk CSV, imported in the Aged Care Provider
  // Portal. It is checked first, so every line that cannot be claimed yet is shown with the reason
  // before anything is downloaded.
  const checkFile = async () => {
    if (!monthClaim) return;
    setBusy('file');
    try {
      const r = await api.get(`/aged-care/claims/${monthClaim.id}/bulk-check`);
      setFileCheck({ ...r.data, period: monthClaim.period, claimId: monthClaim.id });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not check the claim file');
    } finally { setBusy(''); }
  };
  const downloadClaimFile = async () => {
    if (!fileCheck) return;
    setBusy('file-download');
    try {
      const res = await api.get(`/aged-care/claims/${fileCheck.claimId}/bulk-csv`, { responseType: 'blob' });
      saveBlob(res.data, fileCheck.filename || `SAH-${fileCheck.period}.csv`);
      toast.success((fileCheck.files || []).length > 1
        ? `${fileCheck.files.length} invoice files downloaded in a ZIP. Import each one in the Provider Portal as its own invoice.`
        : 'Claim file downloaded. Import it in the Provider Portal: Create invoice, Import CSV.');
    } catch (e) {
      // A blob response hides the JSON error, so read it back out.
      let msg = 'Could not download the claim file';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
      toast.error(msg);
    } finally { setBusy(''); }
  };
  // The worksheet stays available for checking a claim by eye; it is not the file to import.
  const downloadWorksheet = async () => {
    if (!monthClaim) return;
    try {
      const res = await api.get(`/aged-care/claims/${monthClaim.id}/file`, { responseType: 'blob' });
      saveBlob(res.data, `support-at-home-worksheet-${monthClaim.period || period}.csv`);
    } catch (e) {
      // A blob response hides the JSON error, so read it back out.
      let msg = 'No worksheet is available for this claim yet';
      try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
      toast.error(msg);
    }
  };

  // Reconcile needs evidence: the payment statement Services Australia returned, or the amount
  // actually paid typed in by the operator. Both go through here so the outcome is handled once.
  const postReconcile = async (fd) => {
    if (!monthClaim) return;
    setBusy('reconcile');
    try {
      const r = await api.post(`/aged-care/claims/${monthClaim.id}/reconcile`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(r.data?.reconciliation?.linesMatched != null
        ? `Payment statement imported: ${r.data.reconciliation.linesMatched} of ${r.data.reconciliation.linesOnStatement} lines matched`
        : 'Payment statement reconciled');
      if (r.data?.reconciliation) setRecon({ period: monthClaim.period, ...r.data.reconciliation });
      setAfterKey((k) => k + 1);
      setPaidAmount('');
      load();
    } catch (e2) {
      // A 400 NO_EVIDENCE explains why a claim cannot be reconciled against itself; show it as written.
      toast.error(e2.response?.data?.message || 'Could not reconcile the statement');
    } finally {
      setBusy('');
    }
  };
  const onReconcileFile = async e => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || !monthClaim) return;
    const fd = new FormData();
    fd.append('statement', file);
    await postReconcile(fd);
  };
  // An amount typed by hand settles the claim, so it must be a real payment and is checked first.
  const reconcileByAmount = async () => {
    if (!monthClaim) return;
    const amount = Number(paidAmount);
    if (String(paidAmount).trim() === '' || !Number.isFinite(amount) || amount <= 0) {
      return toast.error('Enter the amount Services Australia paid. It must be more than $0.');
    }
    const ok = await confirm(
      `${money(amount)} is recorded as paid against the ${monthLabel(monthClaim.period)} claim of ${money(monthClaim.claimed_amount)}, and the claim is reconciled. Uploading the payment statement instead also gives the breakdown by participant.`,
      { title: 'Record the amount paid?', confirmLabel: `Record ${money(amount)}` },
    );
    if (!ok) return;
    const fd = new FormData();
    fd.append('paid_amount', String(amount));
    await postReconcile(fd);
  };

  // ----- Summary stats from the claim runs -----
  const totalClaimed = claims.reduce((a, c) => a + Number(c.claimed_amount || 0), 0);
  const totalPaid = claims.reduce((a, c) => a + Number(c.paid_amount || 0), 0);
  const totalVariance = totalClaimed - totalPaid;
  const openRuns = claims.filter(c => (c.status || '').toLowerCase() !== 'reconciled').length;

  // Each step's state comes from the month's claim row, so it survives a reload and follows the month
  // picker: closing makes the claim, totalling care management sets fees_accrued, recording
  // contributions sets contributions_done, the portal steps mean the file was imported, and a payment
  // statement settles it. Only "running" is local: the step being posted now. A settled claim takes
  // no more changes, so everything before reconciling counts as done.
  const settled = !!monthClaim && isSettled(monthClaim);
  const derivedSteps = (() => {
    if (!monthClaim) return { close: 'ready', accrue: 'pending', contributions: 'pending', file: 'blocked', reconcile: 'blocked' };
    const accrue = monthClaim.fees_accrued || settled ? 'done' : 'ready';
    const contributions = monthClaim.contributions_done || settled ? 'done' : accrue === 'done' ? 'ready' : 'pending';
    const file = settled || monthClaim.portal_invoice_at || monthClaim.portal_claimed_at || monthClaim.submitted_at ? 'done' : 'ready';
    const reconcile = settled ? 'done' : contributions === 'done' ? 'ready' : 'pending';
    return { close: 'done', accrue, contributions, file, reconcile };
  })();
  const stepState = key => (busy === key ? 'running' : derivedSteps[key] || 'pending');
  // Nothing is posted while the claims are loading or failed to load: the steps would be guessing.
  const unknown = loading || !!loadError;

  const stepDef = [
    {
      key: 'close',
      n: 1,
      icon: Lock,
      title: 'Close actuals',
      desc: 'Lock delivered services and travel for the month so the amounts stop changing.',
      action: runClose,
      // Closed already: closing again refreshes the amount (late visits, corrected travel) until the
      // file is in the Provider Portal or the month is settled.
      btn: monthClaim ? 'Close again' : 'Close actuals',
      // The server refuses a new month while another is submitted and unpaid (the line above says so).
      disabled: () => unknown || busy === 'close' || settled || !!(monthClaim && (monthClaim.portal_invoice_at || monthClaim.portal_claimed_at))
        || !!(openElsewhere && statusOf(openElsewhere) === 'submitted'),
    },
    {
      key: 'accrue',
      n: 2,
      icon: Calculator,
      title: 'Total the care management',
      desc: 'Care management logged with time and a rate is claimed from your pooled care management account. Support at Home has no package management fee.',
      action: runAccrue,
      btn: 'Total care management',
      disabled: () => unknown || busy === 'accrue' || ['pending', 'done'].includes(stepState('accrue')),
    },
    {
      key: 'contributions',
      n: 3,
      icon: Coins,
      title: 'Record participant contributions',
      desc: 'Record how participants pay their contributions. Their contribution invoices are drafted from the amounts Services Australia confirms when you import the payment statement.',
      action: () => setContribModal(true),
      btn: 'Record contributions',
      disabled: () => unknown || busy === 'contributions' || ['pending', 'done'].includes(stepState('contributions')),
    },
    {
      key: 'file',
      n: 4,
      icon: Download,
      title: 'Download the claim file',
      desc: "Services Australia's invoice bulk CSV. In the Aged Care Provider Portal choose Create invoice, then Import CSV, then submit and claim. Lines that cannot be claimed yet are listed with the reason first.",
      action: checkFile,
      btn: 'Check and download',
      // Stays available once done: downloading the file again changes nothing.
      disabled: () => unknown || busy === 'file' || !monthClaim,
    },
    {
      key: 'reconcile',
      n: 5,
      icon: Upload,
      title: 'Import the payment statement',
      desc: "When Services Australia pays, download the payment statement from the Provider Portal (XML is best; CSV works too) and upload it here. Each visit gets the government amount paid and the confirmed contribution, what was drawn from each budget is kept, statements update, and contribution invoices are drafted. You can also enter the amount paid by hand.",
      action: null, // file input
      btn: 'Upload statement',
      disabled: () => unknown || busy === 'reconcile' || !monthClaim,
    },
  ];

  const claimTone = (c) => {
    if (statusOf(c) === 'rejected') return 'rose';
    if (isSettled(c)) return null;
    const due = claimDeadline(c.period);
    if (!due) return null;
    const today = new Date().toISOString().slice(0, 10);
    const days = Math.round((new Date(due) - new Date(today)) / 86400000);
    if (days < 0) return 'rose';
    if (days <= 14) return 'amber';
    return null;
  };

  const claimCols = [
    // The scheme and the day the claim was made sit under the month, so the run fits without a
    // sideways scroll and the portal actions at the end stay in view.
    {
      key: 'period', header: 'Period', sortable: true,
      render: (c) => (
        <span className="block whitespace-nowrap">
          <span className="block font-medium text-slate-800">{monthLabel(c.period)}</span>
          <span className="block text-[11px] text-slate-500">
            {[schemeText(c.scheme), (c.created_at || c.created) && `made ${fmtDate(c.created_at || c.created)}`].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
    },
    {
      key: 'deadline', header: 'Claim by', sortable: true,
      sortValue: (c) => claimDeadline(c.period),
      render: (c) => <Deadline date={claimDeadline(c.period)} done={isSettled(c)} />,
    },
    { key: 'claimed_amount', header: 'Claimed', money: true, sortable: true },
    { key: 'paid_amount', header: 'Paid', money: true, sortable: true },
    {
      key: 'variance', header: 'Variance', money: true, sortable: true, sortValue: varianceOf,
      render: (c) => {
        const v = varianceOf(c);
        return <Currency value={v} className={Math.abs(v) > 0.005 ? 'text-red-600 font-medium' : undefined} />;
      },
    },
    { key: 'status', header: 'Status', render: (c) => <AcStatus status={c.status || 'draft'} /> },
    {
      // The Provider Portal takes two steps: submit the imported invoices, then claim them. Each is
      // recorded here with the day it happened and the portal's own claim reference, which is what
      // Services Australia quotes back. Both can be undone until a payment statement is read.
      key: 'portal', header: 'In the portal',
      render: (c) => {
        const form = portalForm && portalForm.id === c.id ? portalForm : null;
        const working = busy === 'portal-' + c.id;
        const invoiceIds = c.portal_invoice_ids || [];
        const anyStep = !!(c.portal_invoice_at || c.portal_claimed_at);
        return (
          <div className="text-[12px] text-slate-600 min-w-[170px] space-y-1">
            {c.portal_invoice_at && <span className="block">Invoices submitted {fmtDate(c.portal_invoice_at)}</span>}
            {c.portal_claimed_at && <span className="block">Claimed {fmtDate(c.portal_claimed_at)}</span>}
            {c.portal_claim_ref && (
              <span className="block">Portal claim reference <span className="font-mono text-slate-800 select-all">{c.portal_claim_ref}</span></span>
            )}
            {invoiceIds.length > 0 && (
              <span className="block text-slate-500">Portal invoice ID{invoiceIds.length === 1 ? '' : 's'} <span className="font-mono select-all">{invoiceIds.join(', ')}</span></span>
            )}
            {form ? (
              <span className="flex flex-wrap items-center gap-1.5">
                {form.step === 'invoices_submitted' ? (
                  <label className="flex items-center gap-1.5">
                    Submitted on
                    <input type="date" value={form.date} max={sydneyToday()} onChange={(e) => { const v = e.target.value; setPortalForm((p) => ({ ...p, date: v })); }}
                      className="input-field !w-auto !py-1 !text-[12px]" />
                  </label>
                ) : (
                  <input aria-label="Portal claim reference" placeholder="Portal claim reference (optional)" maxLength={40} value={form.claimRef}
                    onChange={(e) => { const v = e.target.value; setPortalForm((p) => ({ ...p, claimRef: v })); }}
                    className="input-field !w-48 !py-1 !text-[12px]" />
                )}
                <button type="button" className="btn-primary btn-sm" disabled={working} onClick={() => savePortalForm(c)}>{working ? 'Saving' : 'Save'}</button>
                <button type="button" className="btn-ghost btn-sm" disabled={working} onClick={() => setPortalForm(null)}>Cancel</button>
              </span>
            ) : !isSettled(c) ? (
              <span className="flex flex-wrap items-center gap-1.5">
                {!c.portal_invoice_at && (
                  <button type="button" className="btn-ghost btn-sm" disabled={working} onClick={() => setPortalForm({ id: c.id, step: 'invoices_submitted', date: sydneyToday(), claimRef: '' })}>Invoices submitted</button>
                )}
                {c.portal_invoice_at && !c.portal_claimed_at && (
                  <button type="button" className="btn-secondary btn-sm" disabled={working} onClick={() => setPortalForm({ id: c.id, step: 'claimed', date: '', claimRef: '' })}>Claimed</button>
                )}
                {anyStep && (
                  <button type="button" className="btn-ghost btn-sm" disabled={working} onClick={() => portalStep(c, 'undo')}>Undo portal steps</button>
                )}
              </span>
            ) : !anyStep && !invoiceIds.length ? <span className="text-slate-300">—</span> : null}
          </div>
        );
      },
    },
    {
      key: 'claim_ref', header: 'Gateway reference',
      render: (c) => (
        <div className="max-w-[220px]">
          {c.claim_ref ? <span className="font-mono text-[12px] text-slate-600 select-all">{c.claim_ref}</span> : <span className="text-slate-300">—</span>}
          {statusOf(c) === 'rejected' && c.rejection_reason && (
            <span className="block text-[12px] text-red-700 mt-0.5">{c.rejection_reason}</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right',
      render: (c) => (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          {!isSettled(c) && (
            <button type="button" onClick={() => submitToGateway(c)} disabled={busy === 'submit-' + c.id} className="btn-primary btn-sm">
              <Upload size={13} /> {busy === 'submit-' + c.id ? 'Submitting' : (c.claim_ref ? 'Resubmit' : 'Submit')}
            </button>
          )}
          {c.claim_ref && statusOf(c) === 'submitted' && (
            <button type="button" onClick={() => pollStatus(c)} disabled={busy === 'poll-' + c.id} className="btn-secondary btn-sm">
              <RefreshCw size={13} /> {busy === 'poll-' + c.id ? 'Checking' : 'Check status'}
            </button>
          )}
        </span>
      ),
    },
  ];

  const matchCols = [
    {
      key: 'participant', header: 'Participant',
      render: (r) => (
        <span>
          {r.participant || '—'}
          {r.not_on_statement && <span className="block text-[11px] text-amber-700">Not mentioned on the statement</span>}
        </span>
      ),
    },
    { key: 'claimed', header: 'Claimed', money: true },
    { key: 'paid', header: 'Paid', money: true },
    {
      key: 'variance', header: 'Variance', money: true,
      render: (r) => <Currency value={r.variance} className={Math.abs(Number(r.variance || 0)) > 0.005 ? 'text-red-600 font-medium' : undefined} />,
    },
  ];

  const gatewayLabel = gw
    ? (gw.gateway === 'file' ? 'Gateway: provider portal, entered by hand' : gw.gateway === 'live' ? 'Gateway: live B2G API' : 'Gateway: simulated, for testing')
    : '';

  return (
    <div>
      <PageHead icon={Landmark}
        title="Claims"
        purpose="Close each month, claim the subsidy from Services Australia in arrears, and reconcile what comes back. A claim is due within 60 days of the end of the quarter."
        actions={<button type="button" onClick={load} className="btn-ghost"><RefreshCw size={15} /> Refresh</button>}
      />

      <FigureStrip>
        <Figure label="Claimed" value={money(totalClaimed)} />
        <Figure label="Paid" value={money(totalPaid)} />
        <Figure label="Variance" value={money(totalVariance)} tone={Math.abs(totalVariance) > 0.005 ? 'red' : 'slate'} />
        <Figure label="Open claim runs" value={openRuns} />
      </FigureStrip>

      <section className="mb-5" aria-label="Period-end run">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-2">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-slate-800">Period-end run</h3>
            <p className="text-[12px] text-slate-500">Work top to bottom: close the month, accrue fees, record contributions, lodge the claim, then reconcile what is paid.</p>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-600">
            Month
            <input
              type="month"
              aria-label="Claim month"
              value={period}
              onChange={e => {
                // What was typed or flagged for one month does not carry over to another.
                setPeriod(e.target.value); setCloseError(null); setPaidAmount('');
                if (late?.for === 'close') { setLate(null); setLateReason(''); setLateCode(''); }
              }}
              className="input-field !w-auto !rounded-lg !py-1.5 !text-[13px]"
            />
          </label>
        </div>
        {loadError && (
          <div className="mb-2"><LoadError message={`${loadError}. The steps below wait until the claims load.`} onRetry={load} /></div>
        )}
        {!loadError && !loading && openElsewhere && (
          <p className="text-[12px] text-slate-600 mb-2">
            The {monthLabel(openElsewhere.period)} claim is not settled yet, and another month cannot be closed until it is.{' '}
            <button type="button" className="text-primary-700 underline" onClick={() => setPeriod(openElsewhere.period)}>Show {monthLabel(openElsewhere.period)}</button>
          </p>
        )}
        <RailList>
          {stepDef.map(step => {
            const Icon = step.icon;
            const m = STEP_STATE[stepState(step.key)] || STEP_STATE.pending;
            const disabled = step.disabled();
            const primary = m.severity === 'soon';
            return (
              <RailRow
                key={step.key}
                severity={m.severity}
                title={(
                  <>
                    <span>{step.n}. {step.title}</span>
                    <AcStatus status={m.status} label={m.label} className="ml-2 align-middle" />
                  </>
                )}
                detail={(
                  <>
                    {step.desc}
                    {step.key === 'close' && closeError && (
                      <span className="block mt-1 text-red-800">{closeError}</span>
                    )}
                    {step.key === 'close' && late && (
                      <span className="block mt-2">
                        <span className="block text-amber-800">This claim was due by {fmtDate(late.deadline)}. Choose Services Australia's reason for lodging late{late.for !== 'close' ? ', then submit it again' : ', then close actuals again'}.</span>
                        {lateCodesError && <span className="block mt-1"><LoadError message={lateCodesError} onRetry={loadLateCodes} /></span>}
                        <select value={lateCode} onChange={e => setLateCode(e.target.value)} aria-label="Late reason" className="input-field mt-1 max-w-sm">
                          <option value="">Choose a reason</option>
                          {Object.entries(lateCodes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <textarea rows={2} value={lateReason} onChange={e => setLateReason(e.target.value)} aria-label="Explanation for claiming late"
                          className="input-field mt-1" placeholder={lateCode === 'OTHER' ? 'Explain the reason (required for Other)' : 'Explanation (optional)'} />
                      </span>
                    )}
                    {step.key === 'file' && monthClaim && (
                      <>
                        <button type="button" onClick={downloadWorksheet} className="block mt-1 text-[12px] text-primary-700 underline">Download the worksheet instead, for checking by eye</button>
                        <button type="button" className="block mt-1 text-[12px] text-primary-700 underline" onClick={async () => {
                          try { const res = await api.get(`/aged-care/claims/${monthClaim.id}/evidence.csv`, { responseType: 'blob' }); saveBlob(res.data, `claim-evidence-${monthClaim.period || period}.csv`); }
                          catch (e) {
                            let msg = 'Could not build the evidence list';
                            try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* keep the default */ }
                            toast.error(msg);
                          }
                        }}>Evidence for each line (clock-in, location, note)</button>
                      </>
                    )}
                  </>
                )}
                right={step.key === 'reconcile' ? (
                  <span className="flex items-center gap-2 flex-wrap justify-end">
                    <input
                      type="number" min="0" step="0.01" placeholder="Amount paid" aria-label="Amount paid"
                      value={paidAmount} onChange={e => setPaidAmount(e.target.value)} disabled={disabled}
                      className="input-field !w-32 !rounded-lg !py-1.5 !text-[13px]"
                    />
                    <button type="button" onClick={reconcileByAmount} disabled={disabled} className="btn-secondary btn-sm">
                      {busy === 'reconcile' ? 'Reconciling' : 'Record amount paid'}
                    </button>
                    <label className={`${primary ? 'btn-primary' : 'btn-secondary'} btn-sm ${disabled ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                      <Upload size={14} /> {busy === 'reconcile' ? 'Uploading' : step.btn}
                      <input type="file" accept=".csv,.txt,.xml,.pdf" className="hidden" disabled={disabled} onChange={onReconcileFile} />
                    </label>
                  </span>
                ) : (
                  <button type="button" onClick={step.action} disabled={disabled} className={`${primary ? 'btn-primary' : 'btn-secondary'} btn-sm`}>
                    <Icon size={14} /> {busy === step.key ? 'Working' : step.btn}
                  </button>
                )}
              />
            );
          })}
        </RailList>
        <p className="text-[12px] text-slate-500 mt-2">
          Claims reach Services Australia through the Aged Care Provider Portal (import the claim file) or B2G software, never through the NDIS bulk payment channel. Once a claim is submitted, the portal takes no further claim until that one is approved.
        </p>
      </section>

      {gw && (
        <Panel
          title="Lodgement channel"
          className="mb-5"
          bodyClass="px-4 py-3"
          actions={(
            <button type="button" onClick={() => setShowB2g(v => !v)} className="btn-ghost btn-sm">
              {showB2g ? 'Hide claiming settings' : 'Claiming settings'}
            </button>
          )}
        >
          <div className="flex flex-wrap items-center gap-3 text-[12px]">
            <span className={gw.live ? 'badge badge-green' : 'badge badge-gray'}>{gatewayLabel}</span>
            {gw.noi_status && <span className="text-slate-500">Notice of Integration: {gw.noi_status}</span>}
            {gw.message && <span className="text-slate-500">{gw.message}</span>}
          </div>
          {showB2g && (
            <div className="mt-3">
              <B2gSettings onSaved={loadGateway} />
            </div>
          )}
        </Panel>
      )}
      {!gw && gwError && (
        <Panel title="Lodgement channel" className="mb-5" bodyClass="px-4 py-3">
          <LoadError message={gwError} onRetry={loadGateway} />
        </Panel>
      )}

      <Register
        title="Claim runs"
        count={claims.length}
        columns={claimCols}
        rows={claims}
        rowKey="id"
        loading={loading}
        error={loadError} onRetry={load}
        rowTone={claimTone}
        empty={{
          icon: FileText,
          title: 'No claim runs yet',
          hint: 'Close a month above and its claim run appears here, ready to lodge with Services Australia.',
        }}
      />

      <AfterClaiming claims={claims} refreshKey={afterKey} />

      {recon && (
        <DetailDrawer
          open
          onClose={closeRecon}
          title={`Reconciliation for ${recon.period ? monthLabel(recon.period) : 'this claim'}`}
          icon={FileText}
          meta={[
            recon.source ? { label: 'Paid amount from', value: recon.source } : null,
            { label: 'Claimed', value: money(recon.claimed), money: true },
            { label: 'Paid', value: money(recon.paid), money: true },
            { label: 'Variance', value: money(recon.variance), money: true },
            recon.unanswered ? { label: 'Not on the statement', value: recon.unanswered } : null,
          ]}
          width="lg"
          footer={<button type="button" onClick={closeRecon} className="btn-ghost">Close</button>}
        >
          <div className="space-y-4">
            {recon.note && (
              <Panel tone="muted" bodyClass="px-4 py-3 text-[13px] text-slate-700">{recon.note}</Panel>
            )}
            {(recon.followups || recon.amendmentsSettled > 0) && (
              <Panel tone="muted" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-1">
                {recon.followups && <p>{recon.followups.opened} line{recon.followups.opened === 1 ? '' : 's'} added to follow up (paid short, not paid, not matched or missing), {recon.followups.resolved} resolved. See After claiming below.</p>}
                {recon.amendmentsSettled > 0 && <p>{recon.amendmentsSettled} correction{recon.amendmentsSettled === 1 ? '' : 's'} settled by this statement.</p>}
                {recon.offsets && Math.abs(Number(recon.offsets.unexplained || 0)) > 0.005 && (
                  <p className="text-amber-800">{money(Math.abs(recon.offsets.unexplained))} {recon.offsets.unexplained < 0 ? 'less' : 'more'} was paid than the statement's own figures explain.</p>
                )}
                {recon.offsets && (recon.offsets.otherCategories || []).length > 0 && (
                  <p className="text-amber-800">The statement has amounts that are not a budget: {recon.offsets.otherCategories.map((c) => `${c.description || 'an amount with no description'} (${money(c.amount)})`).join(', ')}. This can be a special payment or its recovery.</p>
                )}
                {recon.afterError && <p className="text-red-800">{recon.afterError}</p>}
              </Panel>
            )}
            {Number(recon.heldoverPrevious) > 0 && (
              <Panel tone="muted" bodyClass="px-4 py-3 text-[13px] text-slate-700">
                Services Australia kept back <Currency value={recon.heldoverPrevious} /> held over from an earlier period, so less was paid into the bank than the statement's claim total.
              </Panel>
            )}
            {(recon.budgetsDrawn || []).length > 0 && (
              <Panel title="Drawn from participants' budgets" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-1.5">
                {recon.budgetsDrawn.map((b) => (
                  <div key={b.code} className="flex items-baseline justify-between gap-4">
                    <span>{budgetWords(b.code, b.text)}</span>
                    <Currency value={b.drawn} />
                  </div>
                ))}
                <p className="text-[12px] text-slate-500 pt-1">As Services Australia counts it, from the {recon.format === 'xml' ? 'XML' : 'CSV'} statement. Each participant's budgets now show this beside Rostery's own figure.</p>
              </Panel>
            )}
            {(recon.outsideRostery || []).length > 0 && (
              <Panel title="Claimed, but not from Rostery" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-1.5">
                {recon.outsideRostery.map((o, i) => (
                  <div key={`o${i}`} className="flex items-baseline justify-between gap-4">
                    <span>{o.participant || 'Unknown care recipient'}{o.known ? '' : <span className="text-amber-800"> (no participant here has this care recipient ID)</span>}: {o.lines} line{o.lines === 1 ? '' : 's'}</span>
                    <Currency value={o.amount} />
                  </div>
                ))}
                <p className="text-[12px] text-slate-500 pt-1">These came off the participant's budget even though they were not raised here, for example a claim keyed into the portal by hand. They count in the budget figures from Services Australia.</p>
              </Panel>
            )}
            <Panel title="Participants in this claim">
              <DataTable
                columns={matchCols}
                rows={recon.matched || []}
                rowKey={(r, i) => `${r.participant || 'row'}-${i}`}
                dense
                stickyHeader={false}
                label="Participants in this claim"
                rowTone={(r) => (r.not_on_statement ? 'amber' : null)}
                empty={{
                  icon: FileText,
                  title: 'No participant lines to match',
                  hint: recon.source === 'entered by hand'
                    ? 'An amount entered by hand is matched to the claim total, not to each participant. Upload the payment statement to see the breakdown.'
                    : 'The statement had no participant lines this claim could be matched against.',
                }}
              />
            </Panel>
            {recon.contributionInvoices && (
              <Panel title="Contribution invoices" bodyClass="px-4 py-3 text-[13px] text-slate-700 space-y-1.5">
                {[...(recon.contributionInvoices.created || []), ...(recon.contributionInvoices.updated || [])].map((c, i) => (
                  <div key={`c${i}`} className="flex items-baseline justify-between gap-4">
                    <span>{c.invoice} for {c.participant}, {monthLabel(c.period)}</span>
                    <Currency value={c.total} />
                  </div>
                ))}
                {!(recon.contributionInvoices.created || []).length && !(recon.contributionInvoices.updated || []).length && (
                  <p className="text-slate-500">No contribution is due from any participant on this statement.</p>
                )}
                {(recon.contributionInvoices.skipped || []).map((c, i) => (
                  <p key={`s${i}`} className="text-amber-800">{c.participant}: not invoiced ({c.reason}).</p>
                ))}
                {(recon.contributionInvoices.adjustments || []).map((c, i) => (
                  <p key={`a${i}`} className="text-amber-800">{c.participant}: {c.invoice} was sent for {money(c.was)}, but the confirmed contribution is {money(c.now)}. Raise an adjustment.</p>
                ))}
                <p className="text-[12px] text-slate-500 pt-1">They are drafts in Invoices, under the payer "Aged care contribution". Check and send them with the monthly statement.</p>
              </Panel>
            )}
            {(recon.unmatched || []).length > 0 && (
              <Panel title="Lines on the statement that match no visit here" bodyClass="px-4 py-1 divide-y divide-slate-100">
                {recon.unmatched.map((u, i) => (
                  <div key={i} className="py-2 flex items-baseline justify-between gap-4 text-[13px]">
                    <span className="text-slate-800">{u.participant ? `${u.participant}, ${u.date ? fmtDate(u.date) : ''} ${u.service || ''}` : (u.from_statement || '—')}</span>
                    <Currency value={u.paid != null ? u.paid : u.amount} />
                  </div>
                ))}
              </Panel>
            )}
          </div>
        </DetailDrawer>
      )}

      {fileCheck && (
        <DetailDrawer
          open
          onClose={closeFileCheck}
          title={`Claim file for ${monthLabel(fileCheck.period)}`}
          subtitle="For the Aged Care Provider Portal"
          icon={FileText}
          width="lg"
          meta={[
            { label: 'Lines in the file', value: fileCheck.lines },
            { label: 'Claim value', value: money(fileCheck.total), money: true },
            { label: 'Left out', value: (fileCheck.excluded || []).length },
          ]}
          footer={(
            <>
              <button type="button" onClick={closeFileCheck} className="btn-ghost">Close</button>
              <button type="button" onClick={downloadClaimFile} disabled={!fileCheck.lines || busy === 'file-download'} className="btn-primary">
                <Download size={15} /> {busy === 'file-download' ? 'Downloading' : `Download ${fileCheck.lines} line${fileCheck.lines === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        >
          <div className="space-y-4 text-[13px]">
            <Panel tone="muted" bodyClass="px-4 py-3 text-slate-700 space-y-1">
              <p>In the Aged Care Provider Portal: <b>Create invoice</b>, then <b>Import CSV</b> and choose this file. Check the lines, <b>Submit</b> the invoice, then <b>Claim current invoices</b>.</p>
              <p className="text-slate-500">
                The file name{fileCheck.filename && (fileCheck.files || []).length <= 1 ? <>, <span className="font-mono text-slate-700 select-all">{fileCheck.filename}</span>,</> : null} becomes the invoice reference. Each line carries our reference, which comes back on the payment statement and matches the money to the visit.
              </p>
              <p className="text-slate-500">Import it as it is. If you open it in Excel first, Excel drops the leading zero from care recipient IDs and rewrites dates; if you must edit it, import the columns as text and save as CSV UTF-8.</p>
            </Panel>
            {(fileCheck.files || []).length > 1 && (
              <Panel title={`${fileCheck.files.length} invoices`} bodyClass="px-4 py-1 divide-y divide-slate-100">
                <p className="py-2 text-slate-500">The portal takes one NAPS service per invoice, late lines on their own, and at most 10,000 lines. The download is a ZIP with one file each; import each as its own invoice.</p>
                {fileCheck.files.map((x, i) => (
                  <div key={x.ref} className="py-2">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-slate-900 font-medium">Invoice {i + 1}{x.late ? ', late lines' : ''}</span>
                      <span className="text-slate-500">{x.lines} line{x.lines === 1 ? '' : 's'} · {money(x.total)}</span>
                    </div>
                    <span className="block text-[12px] text-slate-500">
                      {x.naps ? <>NAPS service ID <span className="font-mono">{x.naps}</span> · </> : null}File name <span className="font-mono select-all">{x.ref}.csv</span>
                    </span>
                  </div>
                ))}
              </Panel>
            )}
            {fileCheck.late && (
              <p className="text-amber-800">Some lines are past their claiming period ({fileCheck.deadline ? `${fmtDate(fileCheck.deadline)} for this month` : '60 days after the pathway ended'}), so they go in a separate late invoice with the late reason recorded on the claim, and their evidence must be attached in the portal.</p>
            )}
            {fileCheck.rounded > 0 && (
              <Panel title={`${fileCheck.rounded} line${fileCheck.rounded === 1 ? '' : 's'} rounded to 15 minutes`} bodyClass="px-4 py-2">
                <p className="text-slate-500 mb-1.5">
                  Services Australia only accepts quarter hours, so recorded hours are rounded {fileCheck.roundingRule === 'down' ? 'down' : fileCheck.roundingRule === 'up' ? 'up' : 'to the nearest 15 minutes'}. Change the rule under Claiming settings.
                </p>
                <ul className="space-y-0.5">
                  {(fileCheck.roundedLines || []).map((x, i) => (
                    <li key={i} className="text-slate-700">
                      {x.participant} · {fmtDate(x.date)} · recorded {x.recorded} h, claimed {x.claimed} h
                    </li>
                  ))}
                </ul>
              </Panel>
            )}
            {fileCheck.alreadyPaid > 0 && (
              <p className="text-slate-600">{fileCheck.alreadyPaid} line{fileCheck.alreadyPaid === 1 ? '' : 's'} this month {fileCheck.alreadyPaid === 1 ? 'is' : 'are'} already paid by Services Australia and {fileCheck.alreadyPaid === 1 ? 'is' : 'are'} not claimed again. A change to a paid line is a correction made in the portal (After claiming).</p>
            )}
            {(fileCheck.capWarnings || []).length > 0 && (
              <Panel title="Check before claiming" bodyClass="px-4 py-2 space-y-1">
                {fileCheck.capWarnings.map((w, i) => (
                  <p key={i} className="text-amber-800">{w.participant} · {fmtDate(w.date)}: {w.problem}</p>
                ))}
                <p className="text-[12px] text-slate-500">These limits are not yet confirmed in the AT-HM guidelines, so the lines stay in the file.</p>
              </Panel>
            )}
            {fileCheck.items > 0 && (
              <p className="text-slate-600">{fileCheck.items} line{fileCheck.items === 1 ? '' : 's'} from Items and equipment (equipment, home modifications, consumables, bills) are included with their item details. Upload their evidence files in the portal as well.</p>
            )}
            {(fileCheck.pathwayLines?.RC > 0 || fileCheck.pathwayLines?.EL > 0) && (
              <p className="text-slate-600">
                {fileCheck.pathwayLines.RC > 0 && `${fileCheck.pathwayLines.RC} line${fileCheck.pathwayLines.RC === 1 ? ' falls' : 's fall'} inside a Restorative Care Pathway and ${fileCheck.pathwayLines.RC === 1 ? 'is' : 'are'} claimed from that pathway. `}
                {fileCheck.pathwayLines.EL > 0 && `${fileCheck.pathwayLines.EL} line${fileCheck.pathwayLines.EL === 1 ? ' falls' : 's fall'} inside an End-of-Life Pathway and ${fileCheck.pathwayLines.EL === 1 ? 'is' : 'are'} claimed from that pathway.`}
              </p>
            )}
            {(fileCheck.autoLinked || []).length > 0 && (
              <Panel title="Services linked automatically" bodyClass="px-4 py-3 text-slate-700">
                <p className="mb-1.5 text-slate-500">Check these on the Service list. A wrong service code makes the portal reject the line.</p>
                <ul className="list-disc pl-5 space-y-0.5">{fileCheck.autoLinked.map((a) => <li key={a}>{a}</li>)}</ul>
              </Panel>
            )}
            {(fileCheck.excluded || []).length > 0 ? (
              <Panel title="Left out of the file, and why" bodyClass="px-4 py-1 divide-y divide-slate-100">
                {fileCheck.excluded.map((x, i) => (
                  <div key={i} className="py-2">
                    <span className="text-slate-900 font-medium">{x.participant}</span>
                    <span className="text-slate-500"> · {x.date ? fmtDate(x.date) : 'no date'} · {x.service}</span>
                    <span className="block text-amber-800">{x.problem}</span>
                  </div>
                ))}
              </Panel>
            ) : (
              <p className="text-emerald-800">Every claimable line for this month is in the file.</p>
            )}
          </div>
        </DetailDrawer>
      )}

      {contribModal && (
        <DetailDrawer
          open
          onClose={closeContrib}
          title="Record participant contributions"
          subtitle={`For ${monthLabel(period)}`}
          icon={Coins}
          width="sm"
          footer={(
            <>
              <button type="button" onClick={closeContrib} className="btn-ghost">Cancel</button>
              <button type="button" onClick={submitContributions} disabled={busy === 'contributions'} className="btn-primary">
                <CheckCircle2 size={15} /> {busy === 'contributions' ? 'Saving' : 'Record contributions'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <p className="text-[13px] text-slate-600">
              Under Support at Home the claim is for the full price of each service. Services Australia works out each participant's contribution,
              pays you the government share, and confirms the contribution so you can invoice the participant for {monthLabel(period)}.
            </p>
            <Field label="Contribution method">
              {(id) => (
                <select
                  id={id}
                  value={contribForm.method}
                  onChange={e => setContribForm(f => ({ ...f, method: e.target.value }))}
                  className="input-field"
                >
                  <option value="invoice">Invoiced to each participant</option>
                  <option value="direct_debit">Direct debit</option>
                  <option value="none">No contributions this month (clinical care only, or fully subsidised)</option>
                  <option value="income_test">Legacy Home Care Package: income-tested care fee</option>
                  <option value="basic_daily">Legacy Home Care Package: basic daily fee</option>
                </select>
              )}
            </Field>
            <Field label="Notes" hint="Optional">
              {(id) => (
                <textarea
                  id={id}
                  rows={3}
                  value={contribForm.notes}
                  onChange={e => setContribForm(f => ({ ...f, notes: e.target.value }))}
                  className="input-field"
                  placeholder="For example, an updated income assessment received from Services Australia"
                />
              )}
            </Field>
          </div>
        </DetailDrawer>
      )}
    </div>
  );
}
